/**
 * Один SVG маркера на все экраны.
 * Файл читается один раз и растеризуется в три цвета. Карта рисует их
 * одним canvas, поворачивая спрайт вокруг центра точки.
 */

const MARKER_URL = '/images/marker.svg?v=30x21';
const VIEW_X = 0;
const VIEW_Y = 0;
const VIEW_W = 30;
const VIEW_H = 21;
const PIN_VX = 15;
const PIN_VY = 14;
const CIRCLE_R_UNITS = 6.6;
const DISPLAY_HEIGHT = VIEW_H;
const SCALE = DISPLAY_HEIGHT / VIEW_H;
const DISPLAY_WIDTH = VIEW_W * SCALE;
const CIRCLE_RADIUS = CIRCLE_R_UNITS * SCALE;
const RASTER_SCALE = 4;

const COLORS = {
    idle: '#0C80FE',
    selected: '#16A34A',
    unavailable: '#9CA3AF',
};

const STATE_RANK = {
    unavailable: 0,
    idle: 1,
    selected: 2,
};

const conePoint = (x, y) => ({ x: (x - PIN_VX) * SCALE, y: (y - PIN_VY) * SCALE });
const CONE = [
    [conePoint(2.4, 4.4), conePoint(15, 13.3), conePoint(27.6, 4.3)],
    [conePoint(2.4, 4.4), conePoint(15, 0.2), conePoint(27.6, 4.3)],
];

const PIN_X = (PIN_VX - VIEW_X) * SCALE;
const PIN_Y = (PIN_VY - VIEW_Y) * SCALE;

const normalizeAzimuth = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const azimuth = Number(value);
    return Number.isFinite(azimuth) ? azimuth : null;
};

const pointInTriangle = (x, y, a, b, c) => {
    const cross = (px, py, qx, qy, rx, ry) => (px - rx) * (qy - ry) - (qx - rx) * (py - ry);
    const d1 = cross(x, y, a.x, a.y, b.x, b.y);
    const d2 = cross(x, y, b.x, b.y, c.x, c.y);
    const d3 = cross(x, y, c.x, c.y, a.x, a.y);
    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0;

    return !(hasNeg && hasPos);
};

const hitsMarker = (localX, localY, azimuth) => {
    if ((localX * localX) + (localY * localY) <= CIRCLE_RADIUS * CIRCLE_RADIUS) {
        return true;
    }

    if (azimuth === null) {
        return false;
    }

    return CONE.some(([a, b, c]) => pointInTriangle(localX, localY, a, b, c));
};

const localPoint = (dx, dy, azimuth) => {
    if (!azimuth) {
        return { x: dx, y: dy };
    }

    const rad = azimuth * Math.PI / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    return {
        x: (dx * cos) + (dy * sin),
        y: (-dx * sin) + (dy * cos),
    };
};

const loadImage = (svgText) => new Promise((resolve, reject) => {
    const blob = new Blob([svgText], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
    };
    image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Не удалось растеризовать маркер'));
    };
    image.src = url;
});

const loadSprites = async () => {
    const response = await fetch(MARKER_URL, { cache: 'no-cache' });
    if (!response.ok) {
        throw new Error(`Не удалось загрузить маркер: ${response.status}`);
    }

    const template = (await response.text())
        .replace(/width="[\d.]+"/, `width="${VIEW_W * RASTER_SCALE}"`)
        .replace(/height="[\d.]+"/, `height="${VIEW_H * RASTER_SCALE}"`);
    const entries = await Promise.all(Object.entries(COLORS).map(async ([state, color]) => {
        const image = await loadImage(template.split('#0C80FE').join(color));
        return [state, image];
    }));

    return Object.fromEntries(entries);
};

const paintOrder = (frames) => frames.slice().sort(
    (a, b) => (STATE_RANK[a.state] ?? 1) - (STATE_RANK[b.state] ?? 1),
);

/**
 * @param {google.maps.Map} map
 * @param {{ onHover: (gid: string|null) => void, onClick: (gid: string) => void }} handlers
 */
export const createFrameMarkerLayer = (map, handlers) => {
    let frames = [];
    let sprites = null;
    let canvas = null;
    let originX = 0;
    let originY = 0;
    let hoveredGid = null;
    let pointerOn = false;
    const listeners = [];

    const overlay = new google.maps.OverlayView();

    const viewportOrigin = () => {
        const projection = overlay.getProjection();
        const bounds = map.getBounds();
        if (!projection || !bounds) {
            return null;
        }

        const northEast = projection.fromLatLngToDivPixel(bounds.getNorthEast());
        const southWest = projection.fromLatLngToDivPixel(bounds.getSouthWest());

        return { x: southWest.x, y: northEast.y };
    };

    const markerPoint = (frame) => {
        const projection = overlay.getProjection();
        const origin = viewportOrigin();
        if (!projection || !origin) {
            return null;
        }

        const point = projection.fromLatLngToDivPixel(new google.maps.LatLng(frame.lat, frame.lng));

        return { x: point.x - origin.x, y: point.y - origin.y };
    };

    const findFrame = (x, y) => {
        const ordered = paintOrder(frames);
        for (let index = ordered.length - 1; index >= 0; index -= 1) {
            const frame = ordered[index];
            const point = markerPoint(frame);
            if (!point) {
                continue;
            }

            const local = localPoint(x - point.x, y - point.y, frame.azimuth);
            if (hitsMarker(local.x, local.y, frame.azimuth)) {
                return frame;
            }
        }

        return null;
    };

    const eventPoint = (event) => {
        const origin = viewportOrigin();
        const projection = overlay.getProjection();
        if (!origin || !projection || !event.latLng) {
            return null;
        }

        const point = projection.fromLatLngToDivPixel(event.latLng);

        return { x: point.x - origin.x, y: point.y - origin.y };
    };

    const setPointer = (active) => {
        if (active === pointerOn) {
            return;
        }

        pointerOn = active;
        map.setOptions({ draggableCursor: active ? 'pointer' : null });
    };

    const drawSprite = (context, image, azimuth) => {
        context.save();
        if (azimuth) {
            context.rotate(azimuth * Math.PI / 180);
        }

        if (azimuth === null) {
            context.beginPath();
            context.arc(0, 0, CIRCLE_RADIUS, 0, Math.PI * 2);
            context.clip();
        }

        context.drawImage(image, -PIN_X, -PIN_Y, DISPLAY_WIDTH, DISPLAY_HEIGHT);
        context.restore();
    };

    overlay.onAdd = () => {
        canvas = document.createElement('canvas');
        canvas.style.position = 'absolute';
        canvas.style.pointerEvents = 'none';
        overlay.getPanes().overlayLayer.appendChild(canvas);
    };

    overlay.draw = () => {
        if (!canvas || !sprites) {
            return;
        }

        const origin = viewportOrigin();
        const projection = overlay.getProjection();
        if (!origin || !projection) {
            return;
        }

        originX = origin.x;
        originY = origin.y;
        const mapDiv = map.getDiv();
        const width = mapDiv.clientWidth;
        const height = mapDiv.clientHeight;
        const ratio = window.devicePixelRatio || 1;
        canvas.style.left = `${originX}px`;
        canvas.style.top = `${originY}px`;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);

        const context = canvas.getContext('2d');
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, width, height);

        paintOrder(frames).forEach((frame) => {
            const image = sprites[frame.state] || sprites.idle;
            const point = projection.fromLatLngToDivPixel(new google.maps.LatLng(frame.lat, frame.lng));
            context.save();
            context.translate(point.x - originX, point.y - originY);
            drawSprite(context, image, frame.azimuth);
            context.restore();
        });
    };

    overlay.onRemove = () => {
        canvas?.remove();
        canvas = null;
    };

    const onMouseMove = (event) => {
        const point = eventPoint(event);
        const frame = point ? findFrame(point.x, point.y) : null;
        const gid = frame ? frame.gid : null;
        setPointer(Boolean(frame?.clickable));
        if (gid === hoveredGid) {
            return;
        }

        hoveredGid = gid;
        handlers.onHover(gid);
    };

    const onClick = (event) => {
        const point = eventPoint(event);
        const frame = point ? findFrame(point.x, point.y) : null;
        if (frame?.clickable) {
            handlers.onClick(frame.gid);
            return;
        }
        handlers.onMapClick?.(event);
    };

    const onMouseOut = (event) => {
        const next = event.domEvent?.relatedTarget;
        if (next && map.getDiv().contains(next)) {
            return;
        }

        setPointer(false);
        if (hoveredGid === null) {
            return;
        }

        hoveredGid = null;
        handlers.onHover(null);
    };

    listeners.push(
        map.addListener('mousemove', onMouseMove),
        map.addListener('click', onClick),
        map.addListener('mouseout', onMouseOut),
    );
    overlay.setMap(map);

    loadSprites()
        .then((loaded) => {
            sprites = loaded;
            overlay.draw();
        })
        .catch((error) => {
            console.error(error);
        });

    return {
        setFrames(next) {
            frames = (next || []).map((frame) => ({
                gid: String(frame.gid),
                lat: Number(frame.lat) || 0,
                lng: Number(frame.lng) || 0,
                azimuth: normalizeAzimuth(frame.azimuth),
                state: COLORS[frame.state] ? frame.state : 'idle',
                clickable: Boolean(frame.clickable),
            }));
            overlay.draw();
        },
        destroy() {
            listeners.forEach((listener) => listener.remove());
            setPointer(false);
            overlay.setMap(null);
        },
    };
};
